import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Building2,
  ChevronRight,
  FileText,
  FlaskConical,
  Users,
  Sparkles,
  Award,
  Factory,
} from "lucide-react";
import products from "./product";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
};

const ProductCategory = ({ title, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300"
    >
      <h3 className="text-xl font-semibold mb-4 text-blue-800">{title}</h3>
      <div
        className={`space-y-2 ${isExpanded ? "" : "max-h-48 overflow-hidden"}`}
      >
        {items.map((item, index) => (
          <div key={index} className="p-2 bg-blue-50 rounded-lg">
            <p className="font-medium text-blue-900">{item.name}</p>
            <p className="text-sm text-gray-600">{item.details}</p>
          </div>
        ))}
      </div>
      {items.length > 3 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
        >
          {isExpanded ? "Show Less" : "Show More"}
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            className="inline-block"
          >
            ↓
          </motion.span>
        </button>
      )}
    </motion.div>
  );
};

const App = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const cert1 = "/Env_Cert.pdf";
  const cert2 = "/GOTS_cert.pdf";

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "products", "certificates", "contact"];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="font-sans bg-gradient-to-b from-blue-100 to-blue-50 text-gray-800 min-h-screen">
      <header className="bg-white/80 backdrop-blur-xl py-4 px-6 sticky top-0 z-50 border-b border-blue-100">
        <div className="container mx-auto flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2"
          >
            <img
              src="/kmb_logo.png"
              alt="Logo"
              className="h-16 md:h-20 w-auto"
            />
          </motion.div>

          <button
            className="md:hidden focus:outline-none"
            onClick={() => setShowMenu(!showMenu)}
          >
            {showMenu ? <X size={24} /> : <Menu size={24} />}
          </button>

          <AnimatePresence>
            <motion.nav
              initial={false}
              animate={showMenu ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }}
              className={`${
                showMenu
                  ? "absolute top-full left-0 right-0 bg-white shadow-lg md:shadow-none"
                  : "hidden md:flex"
              } md:items-center md:space-x-8 md:relative md:opacity-100 md:translate-x-0 md:bg-transparent`}
            >
              {[
                { name: "Home", icon: <Building2 size={18} /> },
                { name: "About", icon: <Users size={18} /> },
                { name: "Products", icon: <FlaskConical size={18} /> },
                { name: "Contact", icon: <Phone size={18} /> },
              ].map((item) => (
                <motion.a
                  key={item.name}
                  href={`#${item.name.toLowerCase()}`}
                  whileHover={{ scale: 1.05 }}
                  className={`flex items-center gap-2 py-2 px-6 md:px-4 transition-all duration-300 rounded-full ${
                    activeSection === item.name.toLowerCase()
                      ? "text-blue-700 font-semibold bg-blue-50"
                      : "text-gray-800 hover:text-blue-600 hover:bg-blue-50/50"
                  }`}
                >
                  {item.icon}
                  {item.name}
                </motion.a>
              ))}
            </motion.nav>
          </AnimatePresence>
        </div>
      </header>

      <main>
        <motion.section
          id="home"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-gradient-to-br from-blue-700/90 via-blue-800 to-blue-700/90 text-white py-32 px-4"
        >
          <motion.div
            animate={{
              rotate: 360,
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"
          />

          <div className="container mx-auto text-center relative z-10">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="max-w-3xl mx-auto"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-8"
              >
                <Sparkles className="inline-block text-yellow-400 w-16 h-16" />
              </motion.div>
              <h1 className="text-6xl md:text-7xl font-bold mb-8 tracking-tight">
                Welcome to
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-400">
                  KM Biotech
                </span>
              </h1>
              <p className="text-xl md:text-2xl opacity-90 mb-12 leading-relaxed">
                Pioneering Eco-Friendly Solutions in Textile Auxiliaries &
                Detergents
              </p>
              <motion.a
                href="#about"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-2 bg-white text-blue-900 px-8 py-4 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span>Discover More</span>
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </div>
        </motion.section>

        <motion.section
          id="about"
          className="py-24 px-4 bg-gradient-to-b from-white to-blue-50/30"
        >
          <div className="container mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-center mb-16 text-blue-800"
            >
              About Us
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <Award className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold text-blue-800">
                    Our Legacy
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Established in 2016, KM Biotech emerged from V K Chemtech's
                  foundation in 2006. With over two decades of expertise, we've
                  become South India's trusted pioneer in textile auxiliaries.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold text-blue-800">
                    Our Commitment
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  We're dedicated to innovation and sustainability, developing
                  eco-friendly, formaldehyde-free products that meet
                  international standards while being free from harmful
                  chemicals.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <Factory className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold text-blue-800">
                    Our Facility
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Our state-of-the-art facility is equipped with advanced
                  technology and staffed by highly skilled professionals,
                  ensuring the highest quality standards in every product batch.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold text-blue-800">
                    Our Vision
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  We aim to expand our presence across India while maintaining
                  our commitment to customer satisfaction and technical
                  excellence. Our motto "Win Together" reflects our dedication
                  to long-term partnerships.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        <motion.section
          id="products"
          {...fadeIn}
          className="py-24 px-4 bg-gradient-to-b from-blue-50/30 to-white"
        >
          <div className="container mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-center mb-16 text-blue-800"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <FlaskConical className="w-10 h-10 text-blue-600" />
                <span>Our Products</span>
              </div>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-10">
              {Object.entries(products).map(
                ([category, subcategories], index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="space-y-8"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-semibold text-blue-800">
                        {category}
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {Object.entries(subcategories).map(
                        ([subCategory, items], subIndex) => (
                          <motion.div
                            key={subIndex}
                            whileHover={{ y: -5 }}
                            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                          >
                            <div className="p-6 space-y-4">
                              <div className="flex items-center gap-3 border-b border-blue-100 pb-4">
                                <div className="bg-blue-50 p-2 rounded-lg">
                                  <FlaskConical className="w-5 h-5 text-blue-600" />
                                </div>
                                <h4 className="text-xl font-semibold text-blue-800">
                                  {subCategory}
                                </h4>
                              </div>

                              <div className="space-y-3">
                                {items.map((item, itemIndex) => (
                                  <motion.div
                                    key={itemIndex}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: itemIndex * 0.1 }}
                                    className="group"
                                  >
                                    <div className="bg-gradient-to-r from-blue-50 to-white p-2 rounded-xl group-hover:from-blue-100 group-hover:to-blue-50 transition-all duration-300">
                                      <h5 className="font-medium text-blue-900 mb-2">
                                        {item.name}
                                      </h5>
                                      <p className="text-sm text-gray-600 leading-relaxed">
                                        {item.details}
                                      </p>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )
                      )}
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </motion.section>

        <motion.section
          id="certificates"
          {...fadeIn}
          className="py-32 px-4 bg-gradient-to-b from-white to-blue-50/30"
        >
          <div className="container mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-center mb-16 text-blue-800"
            >
              Our Certifications
            </motion.h2>

            <div className="flex flex-wrap justify-center gap-12">
              {[
                {
                  src: "/Env_Cert.png",
                  alt: "Environment Certificate",
                  href: cert1,
                },
                { src: "/Gots_cert.png", alt: "GOTS Certificate", href: cert2 },
              ].map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="relative group"
                >
                  <a href={cert.href} target="_blank" rel="noopener noreferrer">
                    <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-white p-2">
                      <img
                        src={cert.src}
                        alt={cert.alt}
                        className="w-56 h-auto transform group-hover:scale-105 transition-transform duration-300 rounded-xl"
                      />
                    </div>
                  </a>
                  <p className="mt-4 text-center font-medium text-blue-800">
                    {cert.alt}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          id="contact"
          {...fadeIn}
          className="py-32 px-4 bg-gradient-to-b from-blue-50/30 to-white"
        >
          <div className="container mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-center mb-16 text-blue-800"
            >
              Get in Touch
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {[
                {
                  title: "Head Office",
                  address:
                    "# 313, S.N0. 19 & 20, Ground Floor, S.R. Nilayam, 4 'E' cross, 9th Main, Lakshminarasimaiah Layout, Bagalagunte, Bangalore - 560073",
                  phones: ["+91 90363 52575", "+91 94498 36427"],
                  emails: ["gopi@kmbiotech.in", "jamuna@kmbiotech.in"],
                },
                {
                  title: "Branch Office",
                  address:
                    "# 1-2-244/4, Ground Floor, Sri Ragavendra Nilayam, Street No. 6, Domalguda, Himayath Nagar, Hyderabad - 500029",
                  phones: ["+91 93932 20479", "+91 90363 52575"],
                  emails: ["niranjan@kmbiotech.in"],
                },
              ].map((office, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -5 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Building2 className="text-blue-600 w-6 h-6" />
                    <h3 className="text-2xl font-semibold text-blue-800">
                      {office.title}
                    </h3>
                  </div>

                  <div className="flex items-start gap-3 mb-6">
                    <MapPin className="text-blue-600 w-5 h-5 mt-1 flex-shrink-0" />
                    <address className="not-italic text-gray-600 leading-relaxed">
                      {office.address}
                    </address>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      {office.phones.map((phone, phoneIndex) => (
                        <a
                          key={phoneIndex}
                          href={`tel:${phone}`}
                          className="flex items-center gap-3 text-blue-600 hover:text-blue-700 font-medium group"
                        >
                          <Phone className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          {phone}
                        </a>
                      ))}
                    </div>

                    <div className="space-y-2">
                      {office.emails.map((email, emailIndex) => (
                        <a
                          key={emailIndex}
                          href={`mailto:${email}`}
                          className="flex items-center gap-3 text-blue-600 hover:text-blue-700 font-medium group"
                        >
                          <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          {email}
                        </a>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <a
                href="mailto:info@kmbiotech.in"
                className="inline-flex items-center gap-3 text-blue-600 hover:text-blue-700 font-medium group text-lg"
              >
                <Mail className="w-6 h-6 group-hover:scale-110 transition-transform" />
                info@kmbiotech.in
              </a>
            </motion.div>
          </div>
        </motion.section>
      </main>

      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <img
              src="/kmb_logo.png"
              alt="KM Biotech"
              className="h-16 mx-auto mb-8"
            />
            <p className="text-blue-200 mb-6">
              Pioneering sustainable solutions in textile auxiliaries and
              detergents
            </p>
            <p className="text-sm text-blue-300">
              &copy; {new Date().getFullYear()} KM Biotech. All rights reserved.
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default App;
